// Word timings carried through the edits made to a track.
//
// Every edit in the audio editor is a known change of the time axis: a cut
// pulls everything after it forward, an insert pushes it back, a splice does
// both and brings the donor's words with it. So the timings of the edited
// track can be kept up to date arithmetically, and stored with the saved
// version before any measurement has run. A word that a cut goes through is
// simply dropped — it is damaged anyway.
//
// A word is {a, b, s, e, c, x?}: char span in the text, seconds, confidence.
// Plus, after an edit: `d` — the audio of this word was cut away, so it has no
// length any more; `t` — this word's audio was touched by this session's edits
// (kept in memory only, it tells which per-word verdicts may carry over to the
// saved take).
//
// INVARIANT: the list holds ONE entry per word of the text, in order, and the
// edits below preserve that. It is what lets a word be addressed by its
// ordinal — the anchor for verdicts (see migration 016) — and what lets the
// Nth word be looked up without re-measuring anything.
//
// CommonJS: shared by the browser editor (src/utils/alignEdit.js) and the
// server-side task runner (tasks/), so a splice carries timings the same way
// wherever it is made.

const touched = (w, extra) => ({ ...w, t: 1, ...extra });

// [a,b] cut out of the audio: the words inside lose their length (they are no
// longer spoken), everything after moves up.
function cutWords(words, a, b) {
  const delta = b - a;
  return words.map((w) => {
    if (w.e <= a + 1e-6) return w;
    if (w.s >= b - 1e-6) return { ...w, s: w.s - delta, e: w.e - delta };
    return touched(w, { s: a, e: a, d: 1 });
  });
}

// Only [a,b] kept: what falls outside is no longer spoken.
function trimWords(words, a, b) {
  const len = b - a;
  return words.map((w) => {
    if (w.s >= a - 1e-6 && w.e <= b + 1e-6) return { ...w, s: w.s - a, e: w.e - a };
    return touched(w, { s: w.e <= a ? 0 : len, e: w.e <= a ? 0 : len, d: 1 });
  });
}

// `pieceDur` seconds of audio dropped in at `at`. The text gains no words —
// whatever the piece says, only what is written is addressable — so the words
// after the cursor simply move down.
function insertWords(words, at, pieceDur) {
  return words.map((w) => {
    if (w.e <= at + 1e-6) return w;
    // Dropped INSIDE a word: that word now has something else in the middle of
    // it, so its audio counts as touched. The ones after it merely move.
    if (w.s < at - 1e-6) return touched(w, { e: w.e + pieceDur });
    return { ...w, s: w.s + pieceDur, e: w.e + pieceDur };
  });
}

// [a,b] replaced by `piece` with a crossfade of `fade` seconds on each seam
// (see spliceCrossfade: the piece starts `fade` before a, and the tail resumes
// `fade` into the piece's end). A word inside the cut takes the donor's timing
// when the donor voices that same word, and otherwise goes silent — at the
// end of the word placed before it (a word the donor reads differently sits
// mid-splice; the timeline must not go back in time there).
function spliceWords(words, a, b, pieceDur, fade, pieceWords) {
  const at = a - fade;
  const delta = (pieceDur - 2 * fade) - (b - a);
  const fromPiece = new Map((pieceWords || []).map((w) => [w.a, w]));
  let cursor = a;
  return words.map((w) => {
    if (w.e <= a + 1e-6) return w;
    if (w.s >= b - 1e-6) return { ...w, s: w.s + delta, e: w.e + delta };
    const p = fromPiece.get(w.a);
    if (p) { cursor = Math.max(cursor, p.e + at); return touched(w, { s: p.s + at, e: p.e + at, c: p.c, d: undefined }); }
    return touched(w, { s: cursor, e: cursor, d: 1 });
  });
}

// The donor's words inside its selection [sa,sb], made relative to sa and
// given the char spans of the edited take's own words, so they read as words
// of THIS text. `toEdit(di)` names the edited take's word for donor word di,
// or -1 when it has none (a fragment donor whose text moved on).
function pieceWordsFrom(source, editWords, sa, sb, toEdit) {
  const out = [];
  source.forEach((w, di) => {
    if (w.s < sa - 1e-6 || w.e > sb + 1e-6) return;
    const wi = toEdit(di);
    const ew = wi >= 0 ? editWords[wi] : null;
    if (!ew) return;
    out.push({ a: ew.a, b: ew.b, s: w.s - sa, e: w.e - sa, c: w.c, ...(w.x ? { x: w.x } : {}) });
  });
  return out;
}

// The live words that cover the same text as words wi0..wi1 of the stored
// list — a word whose audio an edit cut away is skipped, so the span is what
// is left of the run. null when nothing of it is still spoken.
function liveSpan(live, stored, wi0, wi1) {
  const A = stored[wi0] && stored[wi0].a;
  const B = stored[wi1] && stored[wi1].b;
  if (A == null || B == null) return null;
  const inRun = live.filter((w) => w.a >= A && w.b <= B && !w.d);
  if (!inRun.length) return null;
  return { start: Math.min(...inRun.map((w) => w.s)), end: Math.max(...inRun.map((w) => w.e)) };
}

// What goes to the server with a saved take: the session-only «touched» flag
// is not part of a measurement.
const wordsForStorage = (words) => words.map(({ t, ...w }) => w);

// Ordinals of the words this session never touched — their audio in the saved
// take is the very same audio, so a verdict left on them still holds.
const untouchedOrdinals = (words) => words
  .map((w, i) => (w.t ? -1 : i))
  .filter((i) => i >= 0);

// Several spans cut out at once (deleteRegions): applied last to first, so
// each span is still where it was measured when its turn comes.
function cutWordsMany(words, spans) {
  const order = [...spans].sort((x, y) => y.start - x.start);
  return order.reduce((w, { start, end }) => cutWords(w, start, end), words);
}

module.exports = {
  cutWords, trimWords, insertWords, spliceWords, pieceWordsFrom, liveSpan,
  wordsForStorage, untouchedOrdinals, cutWordsMany,
};
